/**
 * TOCItem Component
 *
 * Renders a single item in the table of contents with an eye icon for
 * visibility toggling. Provides proper indentation based on heading level
 * and click handling for jump navigation.
 *
 * Features:
 * - Clickable jump link to heading
 * - Eye icon for visibility toggle (edit mode only)
 * - Visual feedback for hidden state
 * - Proper indentation (20px per level)
 * - Hover states for better UX
 * - Long text truncation with title tooltip
 * - Keyboard accessible with focus indicators
 * - Screen reader announcements
 * - Responsive design for mobile
 *
 * Props:
 * - heading: Object with { id, text, level, nestingLevel }
 * - isHidden: Boolean indicating if the heading is currently hidden
 * - onJumpClick: Callback function for jump link clicks
 * - onToggleVisibility: Callback function for visibility toggle (null in view mode)
 * - isEditMode: Boolean indicating if in edit mode
 *
 * Usage:
 *   <TOCItem
 *     heading={{ id: 'intro', text: 'Introduction', level: 2, nestingLevel: 0 }}
 *     isHidden={false}
 *     onJumpClick={(e, id) => scrollToHeading(id)}
 *     onToggleVisibility={(id) => toggleHeading(id)}
 *     isEditMode={true}
 *   />
 *
 * Related Files:
 * - app/javascript/components/blocknote/TableOfContentsBlock.jsx
 */

import React from "react";

/**
 * Eye Icon SVG Component
 *
 * Renders an eye icon (visible state) using inline SVG.
 */
function EyeIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

/**
 * Eye Slash Icon SVG Component
 *
 * Renders an eye-slash icon (hidden state) using inline SVG.
 */
function EyeSlashIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
      />
    </svg>
  );
}

/**
 * Truncate long heading text for display
 *
 * @param {string} text - The heading text
 * @param {number} maxLength - Maximum character length
 * @returns {string} Truncated text
 */
function truncateText(text, maxLength = 100) {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength - 3) + "...";
}

/**
 * Main TOCItem Component
 *
 * Renders a single TOC entry with indentation, jump link, and visibility toggle.
 *
 * @param {Object} props
 * @param {Object} props.heading - Heading data object
 * @param {string} props.heading.id - Unique heading ID
 * @param {string} props.heading.text - Heading text content
 * @param {number} props.heading.level - Heading level (1-6)
 * @param {number} props.heading.nestingLevel - Calculated nesting depth
 * @param {boolean} props.isHidden - Whether the heading is hidden
 * @param {Function} props.onJumpClick - Jump link click handler
 * @param {Function} props.onToggleVisibility - Visibility toggle handler (null in view mode)
 * @param {boolean} props.isEditMode - Whether in edit mode
 */
export default function TOCItem({
  heading,
  isHidden = false,
  onJumpClick,
  onToggleVisibility,
  isEditMode = true,
}) {
  // Calculate indentation based on nesting level
  const indentStyle = {
    paddingLeft: `${heading.nestingLevel * 20}px`,
  };

  // Truncate very long heading text
  const displayText = truncateText(heading.text);
  const isTruncated = displayText !== heading.text;

  /**
   * Handles the eye icon click
   *
   * Stops propagation to prevent triggering the jump link,
   * then calls the visibility toggle handler.
   *
   * @param {Event} e - Click event
   */
  const handleEyeClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleVisibility) {
      onToggleVisibility(heading.id);
    }
  };

  /**
   * Handle keyboard activation of jump link
   *
   * @param {Event} e - Keyboard event
   */
  const handleKeyPress = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onJumpClick(e, heading);
    }
  };

  return (
    <li
      style={indentStyle}
      className="toc-item group flex items-center justify-between gap-2 py-1.5 sm:py-1"
      role="listitem"
    >
      {/* Jump Link */}
      <a
        href={`#${heading.id}`}
        onClick={(e) => onJumpClick(e, heading)}
        onKeyDown={handleKeyPress}
        className={`
          toc-link flex-1 text-sm sm:text-base cursor-pointer transition-colors
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded
          ${
            isHidden
              ? "text-neutral-400 hover:text-neutral-500 line-through"
              : "text-neutral-700 hover:text-neutral-900 hover:underline"
          }
        `}
        aria-label={`Jump to ${heading.text}`}
        title={isTruncated ? heading.text : undefined}
        tabIndex={0}
      >
        <span className="break-words">{displayText}</span>
      </a>

      {/* Eye Icon Toggle - Only shown in edit mode */}
      {isEditMode && onToggleVisibility && (
        <button
          type="button"
          onClick={handleEyeClick}
          className={`
            toc-visibility-toggle flex-shrink-0 p-1.5 sm:p-1 rounded transition-all
            opacity-0 group-hover:opacity-100 focus:opacity-100
            ${
              isHidden
                ? "text-neutral-400 hover:text-neutral-600"
                : "text-neutral-500 hover:text-neutral-700"
            }
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
            min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center
          `}
          aria-label={
            isHidden ? `Show ${heading.text}` : `Hide ${heading.text}`
          }
          aria-pressed={isHidden}
          tabIndex={0}
        >
          {isHidden ? <EyeSlashIcon /> : <EyeIcon />}
        </button>
      )}
    </li>
  );
}
